import React, { useCallback, useRef } from "react";
import type { FC } from "react";

import cn from "classnames";
import { createPortal } from "react-dom";

import {
    DEFAULT_ADJUSTMENT_LOCKED,
    DEFAULT_ALIGNMENT,
    DEFAULT_SIDE,
    DEFAULT_VIEW,
    TOOLTIP_ARIA_LIVE,
    TOOLTIP_ROLE,
    TOOLTIP_TEST_ID,
} from "./constants/values";
import { TOOLTIP_DISPLAY_NAME } from "./utils/core";
import { PopUpTail } from "../../helpers/side/PopUpTail/PopUpTail";
import usePopUpPosition from "../../hooks/usePopUpPosition";
import { extractProps } from "../../utils/react";

import type { TooltipProps } from "./types";
import type { PopUpSide } from "../PopUp/types";

import classes from "./Tooltip.styl";

/**
 * Tooltip. Обертка с заложенными токенами цвета и отступами для всплывающих элементов интерфейса.
 * Используется для подсказок или подсвечивания новых фич
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?node-id=2-87605&t=mol4d9LsIu8GN122-0" target="_blank">Спека</a>
 * @constructor
 */
export const Tooltip: FC<TooltipProps> = (
    props: TooltipProps,
): React.ReactElement | null => {
    const {
        view = DEFAULT_VIEW,
        side = DEFAULT_SIDE,
        alignment = DEFAULT_ALIGNMENT,
        adjustmentLocked = DEFAULT_ADJUSTMENT_LOCKED,
        targetRef,
        open,
        children,
        id,
        onOpen,
        onClose,
        onBeforePositionRecalculation,
        customVarsStyles,
        delay,
        "data-tsid": tsid,
    } = props;
    const { customColorsStyles } = extractProps(props, ["customColorsStyles"]);
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    const toggleClasses = useCallback(
        (force: boolean, position: PopUpSide): void => {
            if (!tooltipRef.current) return undefined;

            const enterClass =
                view !== "primary"
                    ? classes["__animation-bounce-enter"]
                    : classes[`__animation-fade-in-${position}`];
            const exitClass =
                view !== "primary"
                    ? classes["__animation-bounce-exit"]
                    : classes[`__animation-fade-out-${position}`];

            tooltipRef.current.classList.toggle(enterClass, force);
            tooltipRef.current.classList.toggle(exitClass, !force);
        },
        [view],
    );

    const enterAnimationCallback = useCallback(
        (position: PopUpSide) => {
            toggleClasses(true, position);
        },
        [toggleClasses],
    );

    const exitAnimationCallback = useCallback(
        (position: PopUpSide) => {
            toggleClasses(false, position);
        },
        [toggleClasses],
    );

    const {
        position,
        wrapperStyles,
        renderPopUp: renderTooltip,
    } = usePopUpPosition({
        side,
        alignment,
        adjustmentLocked,
        targetRef,
        onOpen,
        onClose,
        onBeforePositionRecalculation,
        open,
        popUpRef: tooltipRef,
        enterAnimationCallback,
        exitAnimationCallback,
        delay,
    });

    const wrapperClasses = cn(
        classes.wrapper,
        classes[`__view-${view}`],
        classes[`__side-${position}`],
        classes[`__alignment-${alignment}`],
        classes[`__placement-${position}-${alignment}`],
        customColorsStyles,
        customVarsStyles,
    );

    if (!renderTooltip && !open) return null;

    return createPortal(
        <div
            id={id}
            className={wrapperClasses}
            style={wrapperStyles}
            ref={tooltipRef}
            role={TOOLTIP_ROLE}
            aria-live={TOOLTIP_ARIA_LIVE}
            data-tsid={tsid || TOOLTIP_TEST_ID}
            data-uikit={TOOLTIP_DISPLAY_NAME}
        >
            <PopUpTail side={position} alignment={alignment} />
            <div className={classes.tooltip}>{children}</div>
        </div>,
        document.body,
    );
};

Tooltip.displayName = TOOLTIP_DISPLAY_NAME;

export default Tooltip;
